<script setup>
import SaveTaskTimeAction from "@frontJs/components/actions/projectManagement/tasks/SaveTaskTimeAction.vue";
import TaskTimeActionsCell from "@frontJs/components/tables/cells/projectManagement/tasks/TaskTimeActionsCell.vue";
import TaskTimeDetailsCell from "@frontJs/components/tables/cells/projectManagement/tasks/TaskTimeDetailsCell.vue";
import { ClockIcon } from "@heroicons/vue/24/outline";
import { PlusIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import LineCardData from "@js/components/cards/LineCardData.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import TaskStatusCell from "@js/components/tables/cells/TaskStatusCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate, formatTime } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  userSessionKey: {
    type: String,
    required: false,
    default: null,
  },
  task: {
    type: Object,
    required: false,
  },
  toValidate: {
    type: Boolean,
    required: false,
    default: false,
  },
  validated: {
    type: Boolean,
    required: false,
    default: false,
  },
  type: {
    type: String,
    required: false,
    default: null,
  },
  status: {
    type: [String, Array],
    required: false,
    default: null,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  fetchUserSession,
  loading,
  table,
  globalData,
  data,
  count,
  pagination,
  pageCount,
  sorting,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "id",
      header: () => t("label.id"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.id,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "tenant.name",
      header: () => t("label.customer"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.tenant.name,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "task.name",
      header: () => t("label.name"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.task.fullName,
          length: 160,
        }),
    }),
    columnHelper.display({
      id: "details",
      header: t("label.details"),
      cell: (cellProps) =>
        h(TaskTimeDetailsCell, {
          row: cellProps.row,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "userFullName",
      header: () => t("label.taskManager"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.user.fullName,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "status",
      header: () => t("label.status"),
      cell: (props) =>
        h(TaskStatusCell, {
          data: props.row.original.task,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "minutes",
      header: () => t("label.invoicedTime"),
      cell: (props) =>
        h(TextCellV2, {
          text: formatTime(
            props.row.original.invoicedHours,
            props.row.original.invoicedMinutes,
          ),
          lineThrough: props.row.original.isFree,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "date",
      header: () => t("label.date"),
      cell: (props) =>
        h(TextCellV2, {
          text: formatDate(props.row.original.date),
        }),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      headerClasses: "justify-center",
      cell: (cellProps) =>
        h(TaskTimeActionsCell, {
          row: cellProps.row,
          task: props.task ? props.task : cellProps.row.original.task,
          callback: updateData,
          toValidate: props.toValidate,
          validated: props.validated,
        }),
    }),
  ],
  updateData,
  columnPinning: {
    right: ['actions']
  }
});

function updateData(callback) {
  loading.value = true;

  axios
    .post(route("front.projectManagement.task.postTaskTimes"), {
      pageIndex: pagination.value.pageIndex,
      pageSize: pagination.value.pageSize,
      sorting: sorting.value,
      globalFilter: globalFilter.value,
      filters: filters.value,
      fetchUserSession: fetchUserSession.value,
      userSessionKey: props.userSessionKey,
      data: {
        taskId: props.task ? props.task.id : null,
        toValidate: props.toValidate,
        validated: props.validated,
        type: props.type,
        status: props.status,
      },
    })
    .then((response) => {
      data.value = response.data.data;
      count.value = response.data.count;
      pagination.value.pageIndex = response.data.pageIndex;
      pagination.value.pageSize = response.data.pageSize;
      pageCount.value = response.data.pageCount;
      sorting.value = response.data.sorting;
      globalFilter.value = response.data.globalFilter;
      filters.value = response.data.filters;
      availableFilters.value = response.data.availableFilters;
      globalData.value = response.data.globalData;
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;

      if (callback) {
        callback();
      }
    });
}

function saveTaskTimeCallback() {
  toast.success(t("toast.successfullyAdded"), { position: "top-right" });
  updateData();
}
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <div
      class="mb-4 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      <LineCardData :title="t('title.invoicedTotalTime')" :icon="ClockIcon">
        {{
          formatTime(
            globalData.invoicedTotalHours,
            globalData.invoicedTotalMinutes,
          )
        }}
      </LineCardData>
    </div>

    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :set-global-filter="setGlobalFilter"
      :available-filters="availableFilters"
      :filters="filters"
      :set-filters="setFilters"
    >
      <template #actions>
        <SaveTaskTimeAction
          v-if="props.task"
          :task="props.task"
          :callback="saveTaskTimeCallback"
        >
          <template #button="{ setIsOpen }">
            <ActionButton
              size="small"
              :callback="
                () => {
                  setIsOpen(true);
                }
              "
            >
              <PlusIcon class="mr-2 h-4 w-4" />
              {{ t("button.add") }}
            </ActionButton>
          </template>
        </SaveTaskTimeAction>
      </template>
    </DataTableLayout>
  </div>
</template>
